/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState, type ReactNode } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Clock,
  Copy,
  Crosshair,
  Droplets,
  ExternalLink,
  Flame,
  Info,
  Layers,
  Loader2,
  Pizza,
  Receipt,
  ScanSearch,
  Scissors,
  Search,
  Shapes,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { JsonView, darkStyles } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";
import type { PizzaType } from "@/api/custom-schema";
import type { EvaluationLayer, EvaluationMetadata, PizzaItem } from "@/api/pizza-checker/schemas";
import { getAsset } from "@/api/pizza-checker/storage/storage";
import { usePizzaEvaluationContext } from "@/context/pizza-evaluation-context";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { getIntlLocale } from "@/i18n/translate";
import { t as statusText } from "@/lib/i18n";
import { BurntModelView } from "@/components/evaluation/burnt-model-view";
import { StatusBadge } from "@/components/status-badge";
import { Input } from "@/components/ui/input";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { copyToClipboard } from "@/lib/clipboard";
import { cn } from "@/lib/utils";
import {
  diagnosePizza,
  formatDelay,
  type DiagnosticsSection,
  type Finding,
} from "./findings";
import {
  KeyValueGrid,
  KeyValueRow,
  ObjectTable,
  Value,
  flattenEntries,
  formatNumber,
  isPlainObject,
} from "./data-view";

/** the section index; `label` is a translation key */
const SECTIONS: { id: DiagnosticsSection | "findings" | "raw"; label: TranslationKey }[] = [
  { id: "findings", label: "diag.section.findings" },
  { id: "timing", label: "diag.section.timing" },
  { id: "record", label: "diag.section.record" },
  { id: "detection", label: "diag.section.detection" },
  { id: "photo", label: "diag.section.photo" },
  { id: "components", label: "diag.section.components" },
  { id: "sauce", label: "diag.section.sauce" },
  { id: "doneness", label: "diag.section.doneness" },
  { id: "saver", label: "diag.section.saver" },
  { id: "slicing", label: "diag.section.slicing" },
  { id: "layers", label: "diag.section.layers" },
  { id: "raw", label: "diag.section.raw" },
];

/** metadata keys a section of its own shows; the rest lands in "Other metadata" */
const HANDLED_METADATA = new Set([
  "classification",
  "neural_classification",
  "traditional_classification",
  "confidence",
  "pizza_detection_evaluation",
  "pizza_roi",
  "center",
  "photo_quality_evaluation",
  "components_evaluation",
  "components_bbs",
  "detected_components",
  "component_count",
  "sauce_map",
  "doneness_evaluation",
  "burnt_model",
  "quarter_results",
  "features_24",
  "pizza_saver_evaluation",
  "slicing_model",
  "processing_times",
  "overall_evaluation",
]);

/** sub-steps of processing_times recorded inside a bigger step */
const STEP_PARENTS: Record<string, string> = {
  blur_cnn_crop: "evaluate_photo_quality",
  blur_cnn_inference: "evaluate_photo_quality",
  obstacle_seg_inference: "evaluate_photo_quality",
  obstacle_seg_postprocess: "evaluate_photo_quality",
};
const stepParent = (step: string, steps: string[]) =>
  STEP_PARENTS[step] ??
  steps.find((other) => other !== step && step.startsWith(`${other}_`)) ??
  null;

/** pizza-backend's step order (pipeline.evaluate_pizza); stored timings may come back sorted by name */
const PIPELINE_ORDER = [
  "load_pizza_image",
  "calculate_crust",
  "pizza_presence",
  "calculate_pizza_properties",
  "evaluate_photo_quality",
  "burnt_cnn",
  "component_detection",
  "slicing_cnn",
  "component_evaluation",
  "overall_evaluation",
];

/** steps in pipeline order (unknown ones after the known), each sub-step right below its parent */
function orderSteps(steps: string[]) {
  const rank = (step: string) => {
    const index = PIPELINE_ORDER.indexOf(step);
    return index === -1 ? PIPELINE_ORDER.length + steps.indexOf(step) : index;
  };
  const parents = steps.filter((step) => !stepParent(step, steps)).sort((a, b) => rank(a) - rank(b));
  return parents.flatMap((parent) => [
    parent,
    ...steps.filter((step) => stepParent(step, steps) === parent),
  ]).concat(
    // sub-steps whose parent was not recorded
    steps.filter((step) => {
      const parent = stepParent(step, steps);
      return parent !== null && !steps.includes(parent);
    }),
  );
}

const scrollToSection = (id: string) =>
  document.getElementById(`diag-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });

const SEVERITY = {
  error: { icon: XCircle, text: "text-red-400", label: "Error" },
  warn: { icon: AlertTriangle, text: "text-amber-400", label: "Warning" },
  info: { icon: Info, text: "text-muted-foreground", label: "Note" },
};

function DiagSection({
  id,
  title,
  icon,
  status,
  findings,
  actions,
  className,
  children,
}: {
  id: string;
  title: string;
  icon: ReactNode;
  status?: string | null;
  findings?: Finding[];
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  const { tp } = useLocalization();
  const worst = findings?.some((f) => f.severity === "error")
    ? "error"
    : findings?.some((f) => f.severity === "warn")
      ? "warn"
      : null;
  return (
    <section id={`diag-${id}`} className={cn("min-w-0 scroll-mt-28 rounded-xl border bg-card", className)}>
      <header className="flex items-center gap-2 border-b px-4 py-2.5">
        <span className="text-muted-foreground">{icon}</span>
        <h3 className="text-sm font-semibold">{title}</h3>
        {status && <StatusBadge status={status} label={statusText(status)} size="xs" quiet={status === "OK"} />}
        {worst && (
          <span
            className={cn("text-xs font-medium", SEVERITY[worst].text)}
            title={findings!.map((f) => f.title).join("\n")}
          >
            {tp("diag.findingCount", findings!.length)}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">{actions}</div>
      </header>
      <div className="flex flex-col gap-3 p-4">{children}</div>
    </section>
  );
}

function CopyJsonButton({ value, what }: { value: unknown; what: string }) {
  const { t } = useLocalization();
  return (
    <button
      type="button"
      onClick={() => copyToClipboard(JSON.stringify(value, null, 2), what)}
      className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
      title={t("diag.copyJson", { what })}
    >
      <Copy className="h-3 w-3" />
      JSON
    </button>
  );
}

function SubHeading({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{children}</h4>
      {aside}
    </div>
  );
}

/** A block that starts folded (long field lists that are rarely the problem). */
function Fold({ title, count, children }: { title: string; count?: number; children: ReactNode }) {
  return (
    <Collapsible className="group">
      <CollapsibleTrigger className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
        <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
        {title}
        {count !== undefined && <span className="opacity-60">({count})</span>}
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2">{children}</CollapsibleContent>
    </Collapsible>
  );
}

function TimingBars({ times }: { times: Record<string, number> }) {
  const total = times.total ?? Object.values(times).reduce((a, b) => a + b, 0);
  const steps = orderSteps(Object.keys(times).filter((step) => step !== "total"));
  return (
    <div className="flex flex-col gap-1">
      {steps.map((step) => {
        const seconds = times[step];
        const child = !!stepParent(step, steps);
        const share = total > 0 ? Math.min(1, seconds / total) : 0;
        return (
          <div key={step} className="grid grid-cols-[minmax(0,13rem)_1fr_4.5rem] items-center gap-3 text-xs">
            <span
              className={cn("truncate font-mono", child ? "pl-4 text-muted-foreground/70" : "text-muted-foreground")}
              title={step}
            >
              {step}
            </span>
            <div className="h-2 rounded-full bg-muted/50">
              <div
                className={cn(
                  "h-2 rounded-full",
                  seconds > 1 ? "bg-amber-400/80" : child ? "bg-muted-foreground/30" : "bg-muted-foreground/55",
                )}
                style={{ width: `${Math.max(share * 100, 0.5)}%` }}
              />
            </div>
            <span className="text-right tabular-nums">{formatSeconds(seconds)}</span>
          </div>
        );
      })}
      <div className="mt-1 grid grid-cols-[minmax(0,13rem)_1fr_4.5rem] items-center gap-3 border-t pt-1.5 text-xs">
        <span className="font-mono font-medium">total</span>
        <span />
        <span className="text-right font-semibold tabular-nums">{formatSeconds(total)}</span>
      </div>
    </div>
  );
}

const fixed = (value: number, digits: number) =>
  new Intl.NumberFormat(getIntlLocale(), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: false,
  }).format(value);
const formatSeconds = (seconds: number | null | undefined) =>
  seconds == null || !Number.isFinite(seconds)
    ? "—"
    : seconds < 1
      ? `${fixed(seconds * 1000, seconds < 0.01 ? 1 : 0)} ms`
      : `${fixed(seconds, 2)} s`;

function LayerOpenButton({ pizzaId, layer }: { pizzaId: string; layer: string }) {
  const { t } = useLocalization();
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => {
        // the asset endpoint needs the token: fetch the image and open it as a blob
        setBusy(true);
        const tab = window.open("", "_blank");
        getAsset(
          { pizza_id: pizzaId, type: layer === "source" ? "source" : layer, scale: 100 },
          { responseType: "blob" },
        )
          .then((blob) => {
            const url = URL.createObjectURL(blob as Blob);
            if (tab) tab.location.href = url;
            else window.open(url, "_blank");
          })
          .catch(() => tab?.close())
          .finally(() => setBusy(false));
      }}
      className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground disabled:opacity-50"
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
      {t("common.open")}
    </button>
  );
}

function RawData({
  sources,
}: {
  sources: { id: string; label: string; data: unknown }[];
}) {
  const { tp, t } = useLocalization();
  const [active, setActive] = useState(sources[0]?.id);
  const [query, setQuery] = useState("");
  const source = sources.find((s) => s.id === active) ?? sources[0];
  const entries = useMemo(() => {
    if (!source || !isPlainObject(source.data)) return [];
    // every leaf as one row, list items addressed by index
    const leaves: { path: string; value: unknown }[] = [];
    const walk = (value: unknown, path: string) => {
      if (Array.isArray(value) && value.some((item) => typeof item === "object" && item !== null)) {
        value.forEach((item, index) => walk(item, `${path}[${index}]`));
      } else if (isPlainObject(value) && Object.keys(value).length > 0) {
        for (const [key, child] of Object.entries(value)) walk(child, path ? `${path}.${key}` : key);
      } else {
        leaves.push({ path, value });
      }
    };
    walk(source.data, "");
    return leaves;
  }, [source]);
  const q = query.trim().toLowerCase();
  const matches = q
    ? entries.filter(
        (entry) =>
          entry.path.toLowerCase().includes(q) || JSON.stringify(entry.value ?? null).toLowerCase().includes(q),
      )
    : [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-md border bg-muted/40 p-0.5">
          {sources.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setActive(s.id)}
              className={cn(
                "rounded px-2.5 py-1 text-xs font-medium",
                s.id === source?.id ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="relative min-w-0 flex-1 basis-48">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={tp("diag.raw.search", entries.length)}
            className="h-8 pl-8 text-xs"
          />
        </div>
        {source && <CopyJsonButton value={source.data} what={source.label} />}
      </div>
      {q ? (
        <div className="max-h-[28rem] overflow-auto rounded-md border">
          <dl className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-4 px-3 py-2 text-sm">
            {matches.slice(0, 500).map((entry) => (
              <KeyValueRow key={entry.path} label={entry.path}>
                <Value value={entry.value} />
              </KeyValueRow>
            ))}
          </dl>
          {matches.length === 0 && <div className="p-3 text-xs text-muted-foreground">{t("diag.raw.noMatch")}</div>}
        </div>
      ) : (
        <div className="max-h-[32rem] overflow-auto rounded-md border text-xs">
          <JsonView
            data={(source?.data as object) ?? {}}
            shouldExpandNode={(level) => level < 2}
            style={{ ...darkStyles, container: "p-3 font-mono bg-transparent" }}
          />
        </div>
      )}
    </div>
  );
}

interface DeveloperDiagnosticsProps {
  pizza?: PizzaItem | null;
  metadata?: EvaluationMetadata | null;
  layers?: EvaluationLayer[] | null;
  evaluatedAt?: string | null;
  evaluationResponse?: unknown;
  pizzaType?: PizzaType | null;
  branchName?: string | null;
}

/**
 * Developer mode: everything stored about the pizza, grouped by pipeline step, with automatic findings on
 * top (contradictions, skipped models, shadow-mode failures, borderline decisions, slow steps), so that a
 * wrong or missing verdict can be traced to the step that caused it.
 */
export function DeveloperDiagnostics({
  pizza,
  metadata,
  layers,
  evaluatedAt,
  evaluationResponse,
  pizzaType,
  branchName,
}: DeveloperDiagnosticsProps) {
  const { t, tp, locale } = useLocalization();
  const { hoveredClass, setHoveredClass } = usePizzaEvaluationContext();
  const meta = (metadata ?? null) as Record<string, any> | null;
  const record = pizza as Record<string, any> | null | undefined;
  const findings = useMemo(
    () => diagnosePizza({ pizza: record, metadata: meta, layers, evaluatedAt }),
    // the findings are texts: rebuilt when the language changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [record, meta, layers, evaluatedAt, locale],
  );
  const bySection = (section: DiagnosticsSection) => findings.filter((f) => f.section === section);

  const photo = meta?.photo_quality_evaluation;
  const components = meta?.components_evaluation;
  const bbs: any[] = meta?.components_bbs ?? [];
  const detectedSame = JSON.stringify(meta?.detected_components ?? null) === JSON.stringify(meta?.components_bbs ?? null);
  const sauce = meta?.sauce_map;
  const slicing = meta?.slicing_model;
  const otherMetadata = meta
    ? Object.fromEntries(Object.entries(meta).filter(([key]) => !HANDLED_METADATA.has(key)))
    : {};
  const captureDelay =
    record?.capture_at && evaluatedAt
      ? (new Date(evaluatedAt).getTime() - new Date(record.capture_at).getTime()) / 1000
      : null;
  const roi: number[] | null = meta?.pizza_roi ?? null;

  const curatedRecordKeys = [
    "id",
    "formatted_id",
    "name",
    "menu_item_external_id",
    "branch_id",
    "order_id",
    "capture_at",
    "capture_count",
    "capture_duration",
    "image_download_duration",
    "kitchen_note",
    "preparation_status",
  ];
  const recordRest = record
    ? Object.fromEntries(
        Object.entries(record).filter(([key]) => !curatedRecordKeys.includes(key) && key !== "evaluation"),
      )
    : {};

  return (
    <div className="flex flex-col gap-3">
      {/* section index: the groups are long, jump to the one a finding points at */}
      <nav
        aria-label={t("diag.sectionsLabel")}
        data-no-swipe
        className="sticky top-12 z-20 -mx-3 flex items-center gap-1.5 overflow-x-auto border-b bg-background/95 px-3 py-2 backdrop-blur [scrollbar-width:none] sm:-mx-4 sm:px-4 [&::-webkit-scrollbar]:hidden"
      >
        <span className="mr-1 shrink-0 text-sm font-semibold">{t("diag.title")}</span>
        {SECTIONS.map((section) => {
          const count = section.id === "findings" ? findings.length : 0;
          return (
            <button
              key={section.id}
              type="button"
              onClick={() => scrollToSection(section.id)}
              className="shrink-0 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              {t(section.label)}
              {count > 0 && <span className="ml-1 tabular-nums text-amber-400">{count}</span>}
            </button>
          );
        })}
      </nav>

      {/* Findings */}
      <DiagSection id="findings" title={t("diag.section.findings")} icon={<ShieldCheck className="h-4 w-4" />}>
        {findings.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-green-400" />
            {t("diag.findings.none")}
          </div>
        ) : (
          <ul className="flex flex-col divide-y divide-border/60">
            {findings.map((finding, index) => {
              const style = SEVERITY[finding.severity];
              const Icon = style.icon;
              return (
                <li key={index}>
                  <button
                    type="button"
                    onClick={() => scrollToSection(finding.section)}
                    className="flex w-full items-start gap-3 py-2 text-left hover:bg-muted/30"
                  >
                    <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", style.text)} />
                    <div className="min-w-0 flex-1">
                      <div className={cn("text-sm", finding.severity !== "info" && "font-medium")}>
                        {finding.title}
                      </div>
                      {finding.detail && <div className="text-xs text-muted-foreground">{finding.detail}</div>}
                    </div>
                    <span className="shrink-0 text-[11px] text-muted-foreground/70">
                      {(() => {
                        const label = SECTIONS.find((s) => s.id === finding.section)?.label;
                        return label ? t(label) : null;
                      })()}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </DiagSection>

      {/* columns, not a grid: the cards differ a lot in height and would leave holes */}
      <div className="gap-3 [column-width:26rem] [&>section]:mb-3 [&>section]:break-inside-avoid">
        {/* Timing */}
        <DiagSection
          id="timing"
          title={t("diag.timing.title")}
          icon={<Clock className="h-4 w-4" />}
          findings={bySection("timing")}
          actions={
            meta?.processing_times && (
              <CopyJsonButton value={meta.processing_times} what={t("diag.section.timing")} />
            )
          }
        >
          <dl className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
            <KeyValueRow label="capture_duration">{formatSeconds(record?.capture_duration)}</KeyValueRow>
            <KeyValueRow label="image_download_duration">{formatSeconds(record?.image_download_duration)}</KeyValueRow>
            <KeyValueRow label="evaluated_at">
              <Value value={evaluatedAt ?? null} />
              {captureDelay !== null && Number.isFinite(captureDelay) && (
                <span className="ml-2 text-xs text-muted-foreground">
                  {t("diag.timing.afterCapture", { delay: formatDelay(Math.max(0, captureDelay)) })}
                </span>
              )}
            </KeyValueRow>
          </dl>
          {meta?.processing_times ? (
            <TimingBars times={meta.processing_times} />
          ) : (
            <div className="text-xs text-muted-foreground">{t("diag.timing.none")}</div>
          )}
        </DiagSection>

        {/* Pizza record */}
        <DiagSection
          id="record"
          title={t("diag.record.title")}
          icon={<Receipt className="h-4 w-4" />}
          status={meta?.overall_evaluation?.status}
          findings={bySection("record")}
          actions={
            record && <CopyJsonButton value={{ ...record, evaluation: undefined }} what={t("diag.record.title")} />
          }
        >
          <dl className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
            {curatedRecordKeys.map((key) => (
              <KeyValueRow key={key} label={key}>
                <Value value={record?.[key] ?? null} />
                {key === "branch_id" && branchName && (
                  <span className="ml-2 text-xs text-muted-foreground">{branchName}</span>
                )}
              </KeyValueRow>
            ))}
            <KeyValueRow label={t("diag.record.pizzaType")}>
              {pizzaType ? `${pizzaType.id}. ${pizzaType.name}` : <Value value={null} />}
            </KeyValueRow>
          </dl>
          <Fold title={t("diag.record.otherFields")} count={Object.keys(recordRest).length}>
            <KeyValueGrid data={recordRest} />
          </Fold>
        </DiagSection>

        {/* Detection & geometry */}
        <DiagSection
          id="detection"
          title={t("diag.detection.title")}
          icon={<Crosshair className="h-4 w-4" />}
          status={meta?.pizza_detection_evaluation?.status}
          findings={bySection("detection")}
        >
          <dl className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
            <KeyValueRow label="classification"><Value value={meta?.classification ?? null} /></KeyValueRow>
            <KeyValueRow label="neural_classification"><Value value={meta?.neural_classification ?? null} /></KeyValueRow>
            {meta?.traditional_classification !== undefined && (
              <KeyValueRow label="traditional_classification"><Value value={meta.traditional_classification} /></KeyValueRow>
            )}
            <KeyValueRow label="confidence"><Value value={meta?.confidence ?? null} /></KeyValueRow>
            <KeyValueRow label="pizza_roi">
              {roi ? (
                <span className="tabular-nums">
                  x {roi[0]}, y {roi[1]}, {roi[2]} × {roi[3]} px
                  <span className="ml-2 text-xs text-muted-foreground">
                    {t("diag.detection.aspect", { value: fixed(roi[2] / roi[3], 3) })}
                  </span>
                </span>
              ) : (
                <Value value={null} />
              )}
            </KeyValueRow>
            <KeyValueRow label="center"><Value value={meta?.center ?? null} /></KeyValueRow>
          </dl>
          {meta?.pizza_detection_evaluation && (
            <>
              <SubHeading>pizza_detection_evaluation</SubHeading>
              <KeyValueGrid data={meta.pizza_detection_evaluation} />
            </>
          )}
        </DiagSection>

        {/* Photo quality */}
        <DiagSection
          id="photo"
          title={t("diag.section.photo")}
          icon={<ScanSearch className="h-4 w-4" />}
          status={photo?.status}
          findings={bySection("photo")}
          actions={photo && <CopyJsonButton value={photo} what={t("diag.section.photo")} />}
        >
          {photo ? (
            <>
              <KeyValueGrid data={photo} omit={["checks"]} />
              {Object.entries(photo.checks ?? {}).map(([key, check]: [string, any]) => (
                <div key={key} className="flex flex-col gap-1.5 rounded-lg bg-muted/25 p-3">
                  <SubHeading
                    aside={
                      <StatusBadge
                        status={check.status}
                        label={statusText(check.status)}
                        size="xs"
                        quiet={check.status === "OK"}
                      />
                    }
                  >
                    {key}
                  </SubHeading>
                  <KeyValueGrid data={check} omit={["status"]} />
                </div>
              ))}
            </>
          ) : (
            <div className="text-xs text-muted-foreground">{t("diag.photo.none")}</div>
          )}
        </DiagSection>

        {/* Components */}
        <DiagSection
          id="components"
          title={t("diag.section.components")}
          icon={<Shapes className="h-4 w-4" />}
          status={components?.base_components?.status}
          findings={bySection("components")}
          actions={
            meta && (
              <CopyJsonButton
                value={{ components_evaluation: components, components_bbs: bbs, component_count: meta.component_count }}
                what={t("diag.section.components")}
              />
            )
          }
        >
          {pizzaType && (
            <dl className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
              <KeyValueRow label={t("diag.components.recipe")}>
                {pizzaType.components?.map((c) => c.name).join(", ") || <Value value={null} />}
              </KeyValueRow>
              <KeyValueRow label="component_count"><Value value={meta?.component_count ?? null} /></KeyValueRow>
            </dl>
          )}
          {components ? (
            <>
              {["base_components", "extras", "removals"]
                .filter((group) => components[group])
                .map((group) => (
                  <div key={group} className="flex flex-col gap-1.5 rounded-lg bg-muted/25 p-3">
                    <SubHeading
                      aside={
                        <StatusBadge
                          status={components[group].status}
                          label={statusText(components[group].status)}
                          size="xs"
                          quiet={components[group].status === "OK"}
                        />
                      }
                    >
                      components_evaluation.{group}
                    </SubHeading>
                    <KeyValueGrid data={components[group]} omit={["status"]} />
                  </div>
                ))}
            </>
          ) : (
            <div className="text-xs text-muted-foreground">{t("diag.components.none")}</div>
          )}
          <SubHeading
            aside={<span className="text-[11px] text-muted-foreground">{t("diag.components.hoverHint")}</span>}
          >
            components_bbs ({bbs.length})
          </SubHeading>
          {bbs.length ? (
            <ObjectTable
              rows={bbs.map((bb) => ({
                class: bb.class ?? bb.label,
                model_class: bb.model_class,
                confidence: bb.confidence,
                point1: bb.point1,
                point2: bb.point2,
                size:
                  bb.point1 && bb.point2
                    ? `${Math.abs(bb.point2[0] - bb.point1[0])} × ${Math.abs(bb.point2[1] - bb.point1[1])}`
                    : null,
              }))}
              render={{
                class: (row) => (
                  <span
                    className={cn(
                      "cursor-default rounded px-1",
                      hoveredClass === row.class && "bg-primary/20 text-foreground",
                    )}
                    onMouseEnter={() => setHoveredClass(row.class)}
                    onMouseLeave={() => setHoveredClass(null)}
                  >
                    {row.class}
                  </span>
                ),
                confidence: (row) => (
                  <span className={cn("tabular-nums", row.confidence < 0.5 && "text-amber-300")}>
                    {formatNumber(row.confidence)}
                  </span>
                ),
              }}
            />
          ) : (
            <div className="text-xs text-muted-foreground">{t("diag.components.noDetections")}</div>
          )}
          {meta?.detected_components !== undefined && (
            <div className="text-xs text-muted-foreground">
              detected_components:{" "}
              {detectedSame ? t("diag.components.detectedSame") : t("diag.components.detectedDiffers")}
            </div>
          )}
          {meta?.detected_components && !detectedSame && (
            <ObjectTable rows={meta.detected_components} />
          )}
        </DiagSection>

        {/* Sauces */}
        <DiagSection
          id="sauce"
          title={t("diag.sauce.title")}
          icon={<Droplets className="h-4 w-4" />}
          findings={bySection("sauce")}
        >
          {sauce ? (
            <>
              <dl className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
                <KeyValueRow label="sauce_coverage"><Value value={sauce.sauce_coverage ?? null} /></KeyValueRow>
                <KeyValueRow label="min_share"><Value value={sauce.min_share ?? null} /></KeyValueRow>
                <KeyValueRow label="classes"><Value value={sauce.classes ?? null} /></KeyValueRow>
              </dl>
              {sauce.shares && Object.keys(sauce.shares).length > 0 ? (
                <ObjectTable
                  rows={Object.entries(sauce.shares as Record<string, number>)
                    .sort((a, b) => b[1] - a[1])
                    .map(([name, share]) => ({ class: name, share, counted: share >= (sauce.min_share ?? 0) }))}
                />
              ) : (
                <div className="text-xs text-muted-foreground">{t("diag.sauce.noShares")}</div>
              )}
              <Fold title={t("diag.allFields")}>
                <KeyValueGrid data={sauce} />
              </Fold>
            </>
          ) : (
            <div className="text-xs text-muted-foreground">{t("diag.sauce.none")}</div>
          )}
        </DiagSection>

        {/* Doneness */}
        <DiagSection
          id="doneness"
          title={t("diag.section.doneness")}
          icon={<Flame className="h-4 w-4" />}
          status={
            meta?.doneness_evaluation
              ? meta.doneness_evaluation.burnt?.status ||
                (meta.doneness_evaluation.raw?.evaluated !== false && meta.doneness_evaluation.raw?.status)
                ? "NOK"
                : "OK"
              : null
          }
          findings={bySection("doneness")}
          actions={
            meta && (
              <CopyJsonButton
                value={{ doneness_evaluation: meta.doneness_evaluation, burnt_model: meta.burnt_model }}
                what={t("diag.section.doneness")}
              />
            )
          }
        >
          {meta?.burnt_model && <BurntModelView model={meta.burnt_model} />}
          {meta?.doneness_evaluation && (
            <>
              <SubHeading>doneness_evaluation</SubHeading>
              <KeyValueGrid data={meta.doneness_evaluation} />
            </>
          )}
          {meta?.burnt_model && (
            <Fold title={t("diag.doneness.allFields", { name: "burnt_model" })}>
              <KeyValueGrid data={meta.burnt_model} />
            </Fold>
          )}
          {meta?.quarter_results && (
            <>
              <SubHeading>{t("diag.legacy", { name: "quarter_results" })}</SubHeading>
              <ObjectTable rows={meta.quarter_results} />
            </>
          )}
          {meta?.features_24 && (
            <Fold title={t("diag.legacy", { name: "features_24" })} count={meta.features_24.length}>
              <Value value={meta.features_24} />
            </Fold>
          )}
          {!meta?.doneness_evaluation && !meta?.burnt_model && (
            <div className="text-xs text-muted-foreground">{t("diag.doneness.none")}</div>
          )}
        </DiagSection>

        {/* Pizza saver */}
        <DiagSection
          id="saver"
          title={t("diag.section.saver")}
          icon={<Pizza className="h-4 w-4" />}
          status={meta?.pizza_saver_evaluation?.status}
          findings={bySection("saver")}
        >
          {meta?.pizza_saver_evaluation ? (
            <KeyValueGrid data={meta.pizza_saver_evaluation} />
          ) : (
            <div className="text-xs text-muted-foreground">{t("diag.saver.none")}</div>
          )}
          {(() => {
            const savers = bbs.filter((bb) => String(bb.class ?? bb.label ?? "").includes("saver"));
            return savers.length ? (
              <ObjectTable rows={savers} />
            ) : (
              <div className="text-xs text-muted-foreground">{t("diag.saver.noDetection")}</div>
            );
          })()}
        </DiagSection>

        {/* Slicing */}
        <DiagSection
          id="slicing"
          title={t("diag.section.slicing")}
          icon={<Scissors className="h-4 w-4" />}
          findings={bySection("slicing")}
          actions={slicing && <CopyJsonButton value={slicing} what={t("diag.section.slicing")} />}
        >
          {slicing ? (
            <KeyValueGrid data={slicing} />
          ) : (
            <div className="text-xs text-muted-foreground">{t("diag.slicing.none")}</div>
          )}
        </DiagSection>

        {/* Layers */}
        <DiagSection
          id="layers"
          title={t("diag.layers.title")}
          icon={<Layers className="h-4 w-4" />}
          findings={bySection("layers")}
        >
          {pizza?.id && (
            <ObjectTable
              rows={[
                { name: "source", type: "source", shape: null },
                ...(layers ?? []).map((layer) => ({ name: layer.name, type: layer.type, shape: layer.shape ?? null })),
              ]}
              columns={["name", "type", "shape", "open"]}
              render={{
                open: (row) => <LayerOpenButton pizzaId={pizza.id} layer={row.name} />,
              }}
            />
          )}
        </DiagSection>

        {Object.keys(otherMetadata).length > 0 && (
          <DiagSection id="other" title={t("diag.other.title")} icon={<Info className="h-4 w-4" />}>
            <KeyValueGrid data={otherMetadata} />
          </DiagSection>
        )}
      </div>

      {/* Raw data */}
      <DiagSection id="raw" title={t("diag.section.raw")} icon={<Search className="h-4 w-4" />}>
        <RawData
          sources={[
            { id: "metadata", label: t("diag.raw.metadata"), data: meta ?? {} },
            { id: "pizza", label: t("diag.raw.pizzaDocument"), data: record ?? {} },
            { id: "response", label: t("diag.raw.response"), data: evaluationResponse ?? {} },
          ]}
        />
        <div className="text-[11px] text-muted-foreground">
          {meta ? tp("diag.raw.metadataFields", flattenEntries(meta).length) : t("diag.raw.noMetadata")}
        </div>
      </DiagSection>
    </div>
  );
}
